import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["status", "attachments"];
var _fromJS;
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { fromJS, Map, List, OrderedMap, Set } from 'immutable';
import Actions from 'flow/actions/';
import { sortStrings } from 'flow/utils/sorter-utils';
import { TICKET_KEYS_DATA_PATHS, eventIsKeptInStore } from 'flow/utils/ticket-utils';
import { keyIsTypeConversationMessage, keyIsTypeConversationMessagePlaceholder } from 'flow/utils/ticket-utils';
import { getTagsFromString, listToMap } from 'flow/utils';
import { eventIsCallRecordEvent } from 'flow/types/TicketEventData';
import { isIncludedInTicketsSortingDataInterface, sortingDataKeys } from 'flow/types/Tickets';
export var initialState = fromJS((_fromJS = {}, _defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_fromJS, TICKET_KEYS_DATA_PATHS.ASSIGNED_LOGGED_IN_USER, Map()), TICKET_KEYS_DATA_PATHS.UNASSIGNED, Map()), TICKET_KEYS_DATA_PATHS.CLOSED, Map()), TICKET_KEYS_DATA_PATHS.ALL_LIST, Map()), TICKET_KEYS_DATA_PATHS.RELATED, Map()), "tempSearchData", Map()), "ticketsData", Map()), "ticketEvents", Map()), "metadata", Map()), "componentsMountStatus", Map()), _defineProperty(_fromJS, "settings", Map())));

// Tab lists are NOT ordered here. The reducer keeps keys-data in a plain
// (key-addressed) Map; ordering is derived at read time by the memoized list
// selectors (Mine/Unassigned/All wrappers), which mirror the server sort. This
// keeps a single source of truth for order and avoids stale stored order on
// in-place updates.

var getTicketsKeysDataPaths = function getTicketsKeysDataPaths(BASE_PATH, EXTRA_PATH) {
  return {
    keysPath: EXTRA_PATH ? [BASE_PATH, EXTRA_PATH, 'keys'] : [BASE_PATH, 'keys'],
    nextPageNumberPath: EXTRA_PATH ? [BASE_PATH, EXTRA_PATH, 'nextPageNumber'] : [BASE_PATH, 'nextPageNumber'],
    apiHasMoreDataPath: EXTRA_PATH ? [BASE_PATH, EXTRA_PATH, 'apiHasMoreData'] : [BASE_PATH, 'apiHasMoreData']
  };
};
var getTicketDataReducedForTicketsKeysPath = function getTicketDataReducedForTicketsKeysPath(ticketData) {
  return sortingDataKeys.reduce(function (result, key) {
    if (ticketData.has(key)) {
      return result.set(key, ticketData.get(key));
    }
    return result;
  }, Map());
};
var getSortingDataPerTabForTicket = function getSortingDataPerTabForTicket(ticketKey) {
  return [[TICKET_KEYS_DATA_PATHS.ASSIGNED_LOGGED_IN_USER, 'keys', ticketKey], [TICKET_KEYS_DATA_PATHS.UNASSIGNED, 'keys', ticketKey], [TICKET_KEYS_DATA_PATHS.CLOSED, 'keys', ticketKey], [TICKET_KEYS_DATA_PATHS.ALL_LIST, 'keys', ticketKey]];
};
var getFullTicketDataPathsForTicket = function getFullTicketDataPathsForTicket(ticketKey) {
  return [['ticketsData', 'byKey', ticketKey], ['tempSearchData', 'byKey', ticketKey]];
};
var getStateWithTicketPropertyUpdated = function getStateWithTicketPropertyUpdated(state, ticketKey, propertyName, value, shouldReplace) {
  var basePaths = isIncludedInTicketsSortingDataInterface(propertyName) ? getFullTicketDataPathsForTicket(ticketKey).concat(getSortingDataPerTabForTicket(ticketKey)) : getFullTicketDataPathsForTicket(ticketKey);
  // Above: if propertyName is part of the reduced ticket data interface (SortingData),
  // we include the paths holding this data for the reduce below, where
  // we update the value in respective path where the ticket exists
  // (as long as the new value is not identical to the one in the store,
  // and the optional shouldReplace(currentValue, value) predicate allows it)

  return basePaths.reduce(function (currentState, basePath) {
    if (!currentState.hasIn(basePath)) return currentState; //ticket does not exist in store, early return
    var propertyPath = [].concat(_toConsumableArray(basePath), [propertyName]);
    var currentValue = currentState.getIn(propertyPath);
    if (currentValue === value) return currentState; //new value identical to existing value, early return
    if (shouldReplace && !shouldReplace(currentValue, value)) return currentState;
    return currentState.setIn(propertyPath, value);
  }, state);
};

// Timestamps may arrive out of order (websocket events racing each other and
// fetches), so sort keys must only ever move forward. Compared as dates: the
// push payload and the REST DTO are not guaranteed to format identically.
var isNewerTimestamp = function isNewerTimestamp(currentValue, value) {
  return !currentValue || new Date(value) > new Date(currentValue);
};

/**
 * Members arrive from the API as an array, so `fromJS` yields a List. Store a Set
 * instead: membership has no order (the Access dialog sorts by name at render) and
 * no duplicates, and the lookup is flat rather than a scan — which matters because
 * a ticket list checks membership once per visible row on every store update.
 */
var withMembersAsSet = function withMembersAsSet(ticket) {
  var members = ticket.get('members');
  return members ? ticket.set('members', members.toSet()) : ticket;
};
var getStateUpdatedForTicketsSearched = function getStateUpdatedForTicketsSearched(state, ticketsReceived, nextPageNumber, apiHasMoreData, overwriteExistingTempSearchData) {
  var ticketsByKeyReceived = ticketsReceived.reduce(function (result, item) {
    return result.set(item.get('key'), item);
  }, OrderedMap() //<-- This here important
  // If using standard Immutable JS Map(), order of the list is not preserved,
  // and for search results we want to use whatever order the backend algorithm has deemed is appropriate
  );
  var stateToReturn = state;
  ticketsByKeyReceived.forEach(function (ticket) {
    var tags = ticket.get('tags');
    if (!tags || tags.size === 0) return;
    var tagsMap = listToMap(tags, function (tag) {
      return tag.get('key');
    });
    stateToReturn = stateToReturn.setIn(['metadata', 'perTicket', ticket.get('key'), 'tagsData'], tagsMap);
  });
  var ticketsByKeyReceivedWithTagsRemovedFromDTOs = ticketsByKeyReceived.map(function (ticket) {
    return withMembersAsSet(ticket["delete"]('tags'));
  });
  if (overwriteExistingTempSearchData) {
    return stateToReturn.setIn(['tempSearchData', 'byKey'], ticketsByKeyReceivedWithTagsRemovedFromDTOs).setIn(['tempSearchData', 'nextPageNumber'], nextPageNumber).setIn(['tempSearchData', 'apiHasMoreData'], apiHasMoreData);
  } else {
    return stateToReturn.mergeIn(['tempSearchData', 'byKey'], ticketsByKeyReceivedWithTagsRemovedFromDTOs).setIn(['tempSearchData', 'nextPageNumber'], nextPageNumber).setIn(['tempSearchData', 'apiHasMoreData'], apiHasMoreData);
  }
};
var getStateUpdatedWithTicketsDataReceived = function getStateUpdatedWithTicketsDataReceived(state, ticketsReceived, nextPageNumberReceived, apiHasMoreDataReceived, TICKETS_KEYS_DATA_BASE_PATH, EXTRA_PATH, shouldAddToAllPath) {
  var ticketsByKeyReceived = listToMap(ticketsReceived, function (ticket) {
    return ticket.get('key');
  });
  var ticketsKeysDataMapReceived = ticketsByKeyReceived.map(getTicketDataReducedForTicketsKeysPath);
  var KEYS_DATA_PATHS = getTicketsKeysDataPaths(TICKETS_KEYS_DATA_BASE_PATH, EXTRA_PATH);
  var ticketsKeysData = state.getIn(KEYS_DATA_PATHS.keysPath, Map()).merge(ticketsKeysDataMapReceived);
  var nextPageNumber = nextPageNumberReceived || state.getIn(KEYS_DATA_PATHS.nextPageNumberPath);
  var apiHasMoreData = apiHasMoreDataReceived !== undefined ? apiHasMoreDataReceived : state.getIn(KEYS_DATA_PATHS.apiHasMoreDataPath);
  var stateToReturn = state;
  ticketsByKeyReceived.forEach(function (ticket) {
    var tags = ticket.get('tags');
    if (!tags || tags.size === 0) return;
    var tagsMap = listToMap(tags, function (tag) {
      return tag.get('key');
    });
    stateToReturn = stateToReturn.setIn(['metadata', 'perTicket', ticket.get('key'), 'tagsData'], tagsMap);
  });
  var ticketsByKeyReceivedWithTagsRemovedFromDTOs = ticketsByKeyReceived.map(function (ticket) {
    return withMembersAsSet(ticket["delete"]('tags'));
  });
  stateToReturn = stateToReturn.mergeIn(['ticketsData', 'byKey'], ticketsByKeyReceivedWithTagsRemovedFromDTOs).setIn(KEYS_DATA_PATHS.keysPath, ticketsKeysData).setIn(KEYS_DATA_PATHS.nextPageNumberPath, nextPageNumber).setIn(KEYS_DATA_PATHS.apiHasMoreDataPath, apiHasMoreData);
  if (!shouldAddToAllPath || TICKETS_KEYS_DATA_BASE_PATH === TICKET_KEYS_DATA_PATHS.ALL_LIST) return stateToReturn;
  return stateToReturn.mergeIn([TICKET_KEYS_DATA_PATHS.ALL_LIST, 'keys'], ticketsKeysDataMapReceived);
};
var getStateWithTicketKeysDataPathsUpdated = function getStateWithTicketKeysDataPathsUpdated(state, ticketKey, ticketState, assignee, ownExtensionKey) {
  var ticketData = state.getIn(['ticketsData', 'byKey', ticketKey], Map());
  var ticketKeysData = getTicketDataReducedForTicketsKeysPath(ticketData);

  /**
   * A few words about this method and the state it returns:
   *
   * This method is used to update the keys data paths when the assignee or state (open/closed) of a ticket is changed.
   * The keys data paths hold which tickets to display in which tickets list (mine/unassigned/all); the order they
   * appear in is derived at read time by the list selectors, not stored here.
   *
   * So:
   * When a ticket goes from state X to state Y, we remove the ticketKeys data from path X and add it to path Y
   * Because of this there's a small risk a scroll to the bottom of respective tickets list might retrieve data
   * already available in the store.
   * Both the risk and impact of this are small enough for it to be worth doing the state juggling we do here, imo.
   * Alternatively, we could settle with only removing the keys data from the path it no longer belongs in,
   * and not adding it to its new proper path...
   * (...at the cost of the user not instantly seeing the ticket in the relevant path should he/she be in that tab
   * when the status/assignee is set)
   *
   * More specifically what can occur (given my thinking is right):
   * Say we have 25 tickets open assigned to logged-in user. Now assume 25 (or more) older tickets with other states
   * are opened and assigned to logged-in user. The data is pushed to us from Top, and we now have 50 tickets
   * in path ASSIGNED_LOGGED_IN_USER, but the same nextPageNumber value as before. Scrolling to the bottom of the list
   * displaying open tickets assigned to logged-in user will then fetch the same (or partially the same) tickets as
   * the last 25 already available in the state.
   */

  var ticketKeysDataAllList = state.getIn([TICKET_KEYS_DATA_PATHS.ALL_LIST, 'keys'], Map()).set(ticketKey, ticketKeysData);
  if (ticketState === 'open' && assignee === ownExtensionKey) {
    var ticketKeysDataOpenAssignedLoggedInUser = state.getIn([TICKET_KEYS_DATA_PATHS.ASSIGNED_LOGGED_IN_USER, 'keys'], Map()).set(ticketKey, ticketKeysData);
    return state.setIn([TICKET_KEYS_DATA_PATHS.ASSIGNED_LOGGED_IN_USER, 'keys'], ticketKeysDataOpenAssignedLoggedInUser).setIn([TICKET_KEYS_DATA_PATHS.ALL_LIST, 'keys'], ticketKeysDataAllList).deleteIn([TICKET_KEYS_DATA_PATHS.UNASSIGNED, 'keys', ticketKey]).deleteIn([TICKET_KEYS_DATA_PATHS.CLOSED, 'keys', ticketKey]);
  } else if (ticketState === 'open' && (!assignee || assignee === 'unassigned')) {
    var ticketKeysDataOpenUnassigned = state.getIn([TICKET_KEYS_DATA_PATHS.UNASSIGNED, 'keys'], Map()).set(ticketKey, ticketKeysData);
    return state.setIn([TICKET_KEYS_DATA_PATHS.UNASSIGNED, 'keys'], ticketKeysDataOpenUnassigned).setIn([TICKET_KEYS_DATA_PATHS.ALL_LIST, 'keys'], ticketKeysDataAllList).deleteIn([TICKET_KEYS_DATA_PATHS.ASSIGNED_LOGGED_IN_USER, 'keys', ticketKey]).deleteIn([TICKET_KEYS_DATA_PATHS.CLOSED, 'keys', ticketKey]);
  } else if (ticketState === 'open' && assignee && assignee !== ownExtensionKey) {
    return state.setIn([TICKET_KEYS_DATA_PATHS.ALL_LIST, 'keys'], ticketKeysDataAllList).deleteIn([TICKET_KEYS_DATA_PATHS.ASSIGNED_LOGGED_IN_USER, 'keys', ticketKey]).deleteIn([TICKET_KEYS_DATA_PATHS.UNASSIGNED, 'keys', ticketKey]).deleteIn([TICKET_KEYS_DATA_PATHS.CLOSED, 'keys', ticketKey]);
  } else if (ticketState === 'closed') {
    return state.setIn([TICKET_KEYS_DATA_PATHS.CLOSED, 'keys', ticketKey], ticketKeysData).setIn([TICKET_KEYS_DATA_PATHS.ALL_LIST, 'keys', ticketKey], ticketKeysData).deleteIn([TICKET_KEYS_DATA_PATHS.ASSIGNED_LOGGED_IN_USER, 'keys', ticketKey]).deleteIn([TICKET_KEYS_DATA_PATHS.UNASSIGNED, 'keys', ticketKey]);
  } else {
    //Shouldn't happen..
    return state.setIn([TICKET_KEYS_DATA_PATHS.ALL_LIST, 'keys'], ticketKeysDataAllList);
  }
};
// eslint-disable-next-line complexity
export var ticketsReducer = function ticketsReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : initialState;
  var action = arguments.length > 1 ? arguments[1] : undefined;
  switch (action ? action.type : null) {
    case Actions.CLEAR_STATE:
      {
        return initialState;
      }
    case Actions.CLEAR_TICKETS_DATA:
      {
        var componentsMountStatus = state.get('componentsMountStatus');
        var settings = state.get('settings');
        return initialState.set('componentsMountStatus', componentsMountStatus).set('settings', settings);
      }
    case Actions.SET_TICKETS_RELATED_DATA:
      {
        var ticketsList = fromJS(action.data);
        var ticketKey = fromJS(action.ticketKey);
        return getStateUpdatedWithTicketsDataReceived(state, ticketsList, action.nextPageNumber, action.apiHasMoreData, TICKET_KEYS_DATA_PATHS.RELATED, ticketKey);
      }
    case Actions.SET_OPEN_TICKETS_ASSIGNED_USER_DATA:
      {
        var _ticketsList = fromJS(action.data);
        return getStateUpdatedWithTicketsDataReceived(state, _ticketsList, action.nextPageNumber, action.apiHasMoreData, TICKET_KEYS_DATA_PATHS.ASSIGNED_LOGGED_IN_USER);
      }
    case Actions.SET_OPEN_TICKETS_UNASSIGNED_DATA:
      {
        var _ticketsList2 = fromJS(action.data);
        return getStateUpdatedWithTicketsDataReceived(state, _ticketsList2, action.nextPageNumber, action.apiHasMoreData, TICKET_KEYS_DATA_PATHS.UNASSIGNED);
      }
    case Actions.SET_CLOSED_TICKETS_DATA:
      {
        var _ticketsList3 = fromJS(action.data);
        return getStateUpdatedWithTicketsDataReceived(state, _ticketsList3, action.nextPageNumber, action.apiHasMoreData, TICKET_KEYS_DATA_PATHS.CLOSED);
      }
    case Actions.SET_TICKETS_ALL_DATA:
      {
        var _ticketsList4 = fromJS(action.data);
        return getStateUpdatedWithTicketsDataReceived(state, _ticketsList4, action.nextPageNumber, action.apiHasMoreData, TICKET_KEYS_DATA_PATHS.ALL_LIST);
      }
    case Actions.SET_TICKETS_SEARCHED_DATA:
      {
        var _ticketsList5 = fromJS(action.data);
        return getStateUpdatedForTicketsSearched(state, _ticketsList5, action.nextPageNumber, action.apiHasMoreData, action.nextPageNumber === undefined);
      }
    case Actions.SET_TICKET_DATA:
      {
        var ticket = fromJS(action.data);
        var ticketState = ticket.get('state');
        var assignee = ticket.get('assignee');
        var ownExtensionKey = action.ownExtensionKey;
        var ticketKeysDataPath;
        if (ticketState === 'open' && assignee === ownExtensionKey) {
          ticketKeysDataPath = TICKET_KEYS_DATA_PATHS.ASSIGNED_LOGGED_IN_USER;
        } else if (ticketState === 'open' && (!assignee || assignee === 'unassigned')) {
          ticketKeysDataPath = TICKET_KEYS_DATA_PATHS.UNASSIGNED;
        } else if (ticketState === 'open' && assignee && assignee !== ownExtensionKey) {
          ticketKeysDataPath = TICKET_KEYS_DATA_PATHS.ALL_LIST;
        } else if (ticketState === 'closed') {
          ticketKeysDataPath = TICKET_KEYS_DATA_PATHS.CLOSED;
        }
        var ticketInList = List([ticket]);
        return getStateUpdatedWithTicketsDataReceived(state, ticketInList, undefined, undefined, ticketKeysDataPath, undefined, true);
      }
    case Actions.SET_TICKET_EVENTS:
      {
        var dynamicData = Map({});
        // Events the client can show neither as themselves nor as unsupported
        // content are not stored, so no selector or date section sees them.
        var eventsToStore = action.data.filter(function (event) {
          var _event$attachments;
          return eventIsKeptInStore(event.DTOSubtype, event.message, (_event$attachments = event.attachments) === null || _event$attachments === void 0 ? void 0 : _event$attachments.length);
        });
        var ticketEventsData = fromJS(eventsToStore.map(function (_ref) {
          var status = _ref.status,
            attachments = _ref.attachments,
            event = _objectWithoutProperties(_ref, _excluded);
          if (status) {
            dynamicData = dynamicData.setIn([event.key, 'deliveryStatus'], status);
          }
          if (attachments && attachments.length > 0) {
            dynamicData = dynamicData.setIn([event.key, 'attachments'], fromJS(attachments));
          }
          if (eventIsCallRecordEvent(event.DTOSubtype)) {
            var _event$callRecord;
            // The CallRecord payload (`callRecord` on the DTO) is upserted into the
            // canonical call-records store on ingest, so keep only what the feed/card
            // needs here — the rest would be a duplicated, drift-prone copy.
            return {
              DTOSubtype: event.DTOSubtype,
              key: event.key,
              timeStamp: event.timeStamp,
              callRecordKey: (_event$callRecord = event.callRecord) === null || _event$callRecord === void 0 ? void 0 : _event$callRecord.key
            };
          }
          return event;
        }));
        var ticketEventsDataMap = listToMap(ticketEventsData, function (event) {
          return event.get('key');
        });
        var _ticketKey = action.ticketKey;
        return state.setIn(['ticketEvents', 'dataStatic', _ticketKey], ticketEventsDataMap).withMutations(function (ticketsState) {
          updateEventDataDynamicState(ticketsState, _ticketKey, dynamicData);
        });
      }
    case Actions.SET_TICKET_EVENT_ATTACHMENTS:
      {
        var _ticketKey2 = action.ticketKey;
        var eventKey = action.eventKey;
        var attachments = fromJS(action.data);
        var eventExistsInStore = state.hasIn(['ticketEvents', 'dataStatic', _ticketKey2, eventKey]);
        if (!eventExistsInStore) return state;
        return state.setIn(['ticketEvents', 'dataDynamic', _ticketKey2, eventKey, 'attachments'], attachments);
      }
    case Actions.SET_TICKET_EVENT_MESSAGE_STATUS:
      {
        var _ticketKey3 = action.ticketKey;
        var messageKey = action.messageKey;
        var _eventExistsInStore = state.hasIn(['ticketEvents', 'dataStatic', _ticketKey3, messageKey]);
        if (!_eventExistsInStore) return state;
        return state.setIn(['ticketEvents', 'dataDynamic', _ticketKey3, messageKey, 'deliveryStatus'], action.messageStatus);
      }
    case Actions.UPDATE_TICKET:
      {
        var _ticketKey4 = action.ticketKey;
        var ticketInStore = state.getIn(['ticketsData', 'byKey', _ticketKey4]);
        if (!ticketInStore) {
          return state;
        }
        return state.setIn(['ticketsData', 'byKey', _ticketKey4], withMembersAsSet(fromJS(action.ticketDTO)));
      }
    case Actions.UPDATE_TICKET_TEMP_SEARCH_DATA:
      {
        var _ticketKey5 = action.ticketKey;
        var _ticketInStore = state.getIn(['tempSearchData', 'byKey', _ticketKey5]);
        if (!_ticketInStore) return state;
        return state.setIn(['tempSearchData', 'byKey', _ticketKey5], withMembersAsSet(fromJS(action.ticketDTO)));
      }
    case Actions.ADD_TICKET_EVENT:
      {
        var data = fromJS(action.eventData);
        var _ticketKey6 = action.ticketKey;
        var actorIsOwnExtension = action.actorIsOwnExtension;
        var dataSource = action.dataSource;
        var _eventKey = data.get('key');
        var _attachments = data.get('attachments');
        var deliveryStatus = data.get('status');
        if (!eventIsKeptInStore(data.get('DTOSubtype'), data.get('message'), _attachments === null || _attachments === void 0 ? void 0 : _attachments.size)) {
          return state;
        }
        if (actorIsOwnExtension && keyIsTypeConversationMessage(_eventKey) && dataSource === 'TOP') {
          var placeholderMessageEventExists = state.getIn(['ticketEvents', 'dataStatic', _ticketKey6], Map()).valueSeq().some(function (event) {
            return keyIsTypeConversationMessagePlaceholder(event.get('key'));
          });
          if (placeholderMessageEventExists) return state;
          // About block above: we check if it's a message sent by logged-in user, and if the data
          // is received from Top. If it is, we check if there are placeholder events
          // in the store, which is the case if the message has been sent from the same client.
          // When this is true, we do an early exit, returning state as is, as we leave the data handling
          // to the API callback (where dataSource will not be set to 'TOP').

          // Without this block, we'd have duplicate events in the case
          // the Top event reaches the client before the API callback does
          // (which does indeed occur at times).
        }
        var ticketEventDataStatic = data["delete"]('status')["delete"]('attachments'); //Setting status and attachments in its own path

        var _dynamicData = fromJS(_defineProperty({}, _eventKey, _objectSpread(_objectSpread({}, _attachments && {
          attachments: _attachments
        }), deliveryStatus && {
          deliveryStatus: deliveryStatus
        })));
        return state.mergeIn(['ticketEvents', 'dataStatic', _ticketKey6, _eventKey], ticketEventDataStatic).withMutations(function (ticketsState) {
          updateEventDataDynamicState(ticketsState, _ticketKey6, _dynamicData);
        });
      }
    case Actions.SET_TICKET_EVENT:
      {
        var _ticketKey7 = action.ticketKey;
        var placeholderKeyToRemove = action.placeholderKeyToRemove;
        var _attachments2 = action.eventData.attachments;
        var _deliveryStatus = action.eventData.status;
        var _dynamicData2 = fromJS(_defineProperty({}, action.eventData.key, _objectSpread(_objectSpread({}, _attachments2 && {
          attachments: _attachments2
        }), _deliveryStatus && {
          deliveryStatus: _deliveryStatus
        })));
        var dataStatic = fromJS(action.eventData)["delete"]('status')["delete"]('attachments');
        return state.withMutations(function (ticketsState) {
          if (placeholderKeyToRemove) {
            ticketsState.deleteIn(['ticketEvents', 'dataStatic', _ticketKey7, placeholderKeyToRemove]).deleteIn(['ticketEvents', 'dataDynamic', _ticketKey7, placeholderKeyToRemove]);
          }
          ticketsState.setIn(['ticketEvents', 'dataStatic', _ticketKey7, action.eventData.key], dataStatic);
          updateEventDataDynamicState(ticketsState, _ticketKey7, _dynamicData2);
        });
      }
    case Actions.UPLOAD_FILE_STAGE_IN_SEND_TICKET_MESSAGE_FINISHED:
      {
        var attachmentData = action.attachmentData;
        if (!attachmentData) return state;
        return state.setIn(['ticketEvents', 'dataDynamic', action.ticketKey, action.placeholderKey, 'attachments'], fromJS([attachmentData]));
      }
    case Actions.SET_TICKET_ASSIGNEE:
      {
        var _ticketKey8 = action.ticketKey;
        var newAssignee = action.assigneeKey;
        var _ownExtensionKey = action.ownExtensionKey;
        var _ticketInStore2 = state.getIn(['ticketsData', 'byKey', _ticketKey8]);
        if (!_ticketInStore2) {
          return state;
        }
        var _ticketState = _ticketInStore2.get('state');

        //TO DO: when lastUpdate becomes relevant, look into updating that value
        var stateWithTicketKeysPathsDataUpdated = getStateWithTicketKeysDataPathsUpdated(state, _ticketKey8, _ticketState, newAssignee, _ownExtensionKey);
        return stateWithTicketKeysPathsDataUpdated.setIn(['ticketsData', 'byKey', _ticketKey8, 'assignee'], newAssignee);
      }
    case Actions.SET_TICKET_STATE:
      {
        var _ticketKey9 = action.ticketKey;
        var newTicketState = action.ticketState;
        var _ownExtensionKey2 = action.ownExtensionKey;
        var _ticketInStore3 = state.getIn(['ticketsData', 'byKey', _ticketKey9]);
        if (!_ticketInStore3) {
          return state;
        }
        var _assignee = _ticketInStore3.get('assignee');

        //TO DO: when lastUpdate becomes relevant, look into updating that value
        var _stateWithTicketKeysPathsDataUpdated = getStateWithTicketKeysDataPathsUpdated(state, _ticketKey9, newTicketState, _assignee, _ownExtensionKey2);
        return _stateWithTicketKeysPathsDataUpdated.setIn(['ticketsData', 'byKey', _ticketKey9, 'state'], newTicketState);
      }
    case Actions.SET_TICKET_AWAITING_ACTIVITY:
      {
        return getStateWithTicketPropertyUpdated(state, action.ticketKey, 'awaitingActivity', action.value);
      }
    case Actions.SET_TICKET_LAST_UPDATED:
      {
        return getStateWithTicketPropertyUpdated(state, action.ticketKey, 'lastUpdate', action.value, isNewerTimestamp);
      }
    case Actions.SET_TICKET_SUBJECT:
      {
        return getStateWithTicketPropertyUpdated(state, action.ticketKey, 'subject', action.value);
      }
    case Actions.SET_TICKET_VISIBILITY:
      {
        return getStateWithTicketPropertyUpdated(state, action.ticketKey, 'visibility', action.value);
      }
    case Actions.SET_TICKET_EVENT_SUMMARY:
      {
        var _data = fromJS(action.data).set('source', action.source);
        return state.setIn(['metadata', action.eventKey, 'summaryData'], _data);
      }
    case Actions.SET_ALL_TAGS_AVAILABLE_FOR_TICKET_TAGGING:
      {
        var tagsData = listToMap(fromJS(action.data), function (tag) {
          return tag.get('key');
        });
        return state.setIn(['metadata', 'allTags'], tagsData);
      }
    case Actions.SET_NEW_TAG_CREATED:
      {
        var tagData = fromJS(action.tagData);
        var tagKey = tagData.get('key');
        return state.setIn(['metadata', 'allTags', tagKey], tagData);
      }
    case Actions.SET_TAGS_TO_TICKET:
      {
        var _tagsData = listToMap(fromJS(action.data), function (tag) {
          return tag.get('key');
        }).sortBy(function (tag) {
          return tag.get('label');
        }, sortStrings);
        return state.setIn(['metadata', 'perTicket', action.ticketKey, 'tagsData'], _tagsData);
      }
    case Actions.ATTACH_TAG_TO_TICKET:
      {
        var _tagData = fromJS(action.data);
        var _tagKey = _tagData.get('key');
        var tagsDataUpdatedAndSorted = state.getIn(['metadata', 'perTicket', action.ticketKey, 'tagsData'], OrderedMap()).set(_tagKey, _tagData).sortBy(function (tag) {
          return tag.get('label');
        }, sortStrings);
        return state.setIn(['metadata', 'perTicket', action.ticketKey, 'tagsData'], tagsDataUpdatedAndSorted);
      }
    case Actions.REMOVE_TAG_FROM_TICKET:
      {
        return state.deleteIn(['metadata', 'perTicket', action.ticketKey, 'tagsData', action.tagKey]);
      }
    case Actions.SET_RENAME_TAG:
      {
        return state.withMutations(function (map) {
          map.getIn(['metadata', 'perTicket'], Map()).forEach(function (metadataData, ticketKey) {
            var tagData = metadataData.getIn(['tagsData', action.tagKey]);
            if (tagData) {
              map.setIn(['metadata', 'perTicket', ticketKey, 'tagsData', action.tagKey, 'label'], action.newName);
            }
          });
          if (map.getIn(['metadata', 'allTags', action.tagKey])) {
            map.setIn(['metadata', 'allTags', action.tagKey, 'label'], action.newName);
          }
        });
      }
    case Actions.DELETE_TAGS_COMPLETELY:
      {
        var keysForTagsToDelete = action.tagKeys;
        return state.withMutations(function (map) {
          map.getIn(['metadata', 'perTicket'], Map()).forEach(function (metadataData, ticketKey) {
            var tagsData = metadataData.get('tagsData', Map());
            var updatedTagsData = tagsData.filter(function (tag) {
              var tagKey = tag.get('key');
              return !keysForTagsToDelete.includes(tagKey);
            });
            map.setIn(['metadata', 'perTicket', ticketKey, 'tagsData'], updatedTagsData);
          });
          keysForTagsToDelete.forEach(function (tagKey) {
            map.deleteIn(['metadata', 'allTags', tagKey]);
          });
        });
      }
    case Actions.SET_TICKET_OPEN_AI_EXPERIMENTAL_TAGS:
      {
        var _tagsData2 = fromJS(action.data);
        var tagsString = _tagsData2.get('text');
        if (!tagsString) return state;
        var tags = getTagsFromString(tagsString);
        var tagsMap = Map(tags.map(function (tag) {
          return [tag, tag];
        }));
        var tagsDataUpdated = _tagsData2.set('tags', tagsMap).set('source', action.source);
        return state.setIn(['metadata', 'perTicket', action.ticketKey, 'openAiExperimentalTagsData'], tagsDataUpdated);
      }
    case Actions.REMOVE_TICKET_OPEN_AI_EXPERIMENTAL_TAG:
      {
        return state.deleteIn(['metadata', 'perTicket', action.ticketKey, 'openAiExperimentalTagsData', 'tags', action.tag]);
      }
    case Actions.APPLY_TICKET_MEMBERS_CHANGE:
      {
        var _ticketKey0 = action.ticketKey;
        var _ticketInStore4 = state.getIn(['ticketsData', 'byKey', _ticketKey0]);
        if (!_ticketInStore4) {
          return state;
        }
        var membersRemoved = action.membersRemoved || [];
        var membersAdded = action.membersAdded || [];

        // Set algebra dedupes on its own, so re-adding an existing member is a
        // no-op rather than something to filter out by hand.
        var membersUpdated = _ticketInStore4.get('members', Set()).subtract(membersRemoved).union(membersAdded);
        return state.setIn(['ticketsData', 'byKey', _ticketKey0, 'members'], membersUpdated);
      }
    case Actions.REMOVE_TICKET:
      {
        var _ticketKey1 = action.ticketKey;

        // Everything the store holds about a ticket the viewer may no longer
        // read. The full-data paths come from the helper rather than being
        // listed again here: tempSearchData is one of them, and selectTicket
        // falls back to it, so a ticket left there survives its own removal.
        var stateWithoutTicket = [].concat(_toConsumableArray(getFullTicketDataPathsForTicket(_ticketKey1)), _toConsumableArray(getSortingDataPerTabForTicket(_ticketKey1)), [[TICKET_KEYS_DATA_PATHS.RELATED, _ticketKey1], ['ticketEvents', 'dataStatic', _ticketKey1], ['ticketEvents', 'dataDynamic', _ticketKey1], ['metadata', 'perTicket', _ticketKey1]]).reduce(function (stateSoFar, path) {
          return stateSoFar.deleteIn(path);
        }, state);

        // It can also sit in another ticket's related list, where it would
        // render a row with nothing behind it.
        return stateWithoutTicket.updateIn([TICKET_KEYS_DATA_PATHS.RELATED], Map(), function (relatedPerTicket) {
          return relatedPerTicket.map(function (relatedData) {
            return relatedData.deleteIn(['keys', _ticketKey1]);
          });
        });
      }
    case Actions.REMOVE_TICKET_EVENT:
      {
        return state.deleteIn(['ticketEvents', 'dataStatic', action.ticketKey, action.eventKey]).deleteIn(['ticketEvents', 'dataDynamic', action.ticketKey, action.eventKey]);
      }
    case Actions.SET_TICKETS_BADGE_COUNT:
      {
        var count = action.count;
        return state.setIn(['ticketsData', 'ticketsBadgeCount'], count);
      }
    case Actions.SET_TICKETS_OPEN_ASSIGNED_USER_COUNT_TOTAL:
      {
        var _count = action.count;
        return state.setIn(['ticketsData', 'totalNumberOfTicketsOpenAssignedToUser'], _count);
      }
    case Actions.INCREMENT_TICKETS_OPEN_ASSIGNED_USER_COUNT_TOTAL:
      {
        var currentCount = state.getIn(['ticketsData', 'totalNumberOfTicketsOpenAssignedToUser'], 0);
        var newCount = currentCount + 1;
        return state.setIn(['ticketsData', 'totalNumberOfTicketsOpenAssignedToUser'], newCount);
      }
    case Actions.DECREMENT_TICKETS_OPEN_ASSIGNED_USER_COUNT_TOTAL:
      {
        var _currentCount = state.getIn(['ticketsData', 'totalNumberOfTicketsOpenAssignedToUser'], 0);
        var _newCount = _currentCount - 1;
        return state.setIn(['ticketsData', 'totalNumberOfTicketsOpenAssignedToUser'], _newCount);
      }
    case Actions.SET_TICKETS_LIST_MOUNT_STATUS:
      {
        var isMounted = action.isMounted;
        var tabSelected = action.tabSelected;
        if (isMounted) {
          return state.setIn(['componentsMountStatus', 'ticketsList', 'isMounted'], true).setIn(['componentsMountStatus', 'ticketsList', 'tabSelected'], tabSelected);
        } else {
          return state.deleteIn(['componentsMountStatus', 'ticketsList']);
        }
      }
    case Actions.SET_TICKET_VIEW_MOUNT_STATUS:
      {
        var _isMounted = action.isMounted;
        var _ticketKey10 = action.ticketKey;
        if (_isMounted) {
          return state.setIn(['componentsMountStatus', 'ticketView', _ticketKey10], fromJS({
            context: action.context,
            ticketKey: _ticketKey10
          }));
        } else {
          return state.deleteIn(['componentsMountStatus', 'ticketView', _ticketKey10]);
        }
      }
    case Actions.SET_TICKET_EVENTS_MOUNT_STATUS:
      {
        if (action.isMounted) {
          return state.setIn(['componentsMountStatus', 'ticketEvents', action.ticketKey], true);
        } else {
          return state.deleteIn(['componentsMountStatus', 'ticketEvents', action.ticketKey]);
        }
      }
    case Actions.SET_TICKETS_SETTINGS_FOR_QUEUE:
      {
        var ticketsSettingsDTO = fromJS(action.data);
        return state.setIn(['settings', 'perQueue', action.queueEntityKey], ticketsSettingsDTO);
      }
    case Actions.SET_TICKET_KEY_FOR_LOCUTUS_SESSION:
      {
        // A queue call's ticket can be created after its leg already rings.
        // The leg then never carries the ticket id in its bag variables, so
        // the Mine list joins the two on the locutus session id instead.
        return state.setIn(['ticketKeysByLocutusSessionId', action.locutusSessionId], action.ticketKey);
      }
    case Actions.SET_DEFAULT_TICKET_VISIBILITY_FOR_QUEUE:
      {
        var defaultTicketVisibilityDTO = fromJS(action.data);
        return state.setIn(['settings', 'defaultVisibilityPerQueue', action.queueEntityKey], defaultTicketVisibilityDTO);
      }
    case Actions.SET_TICKETS_TAB_ENFORCED:
      {
        return state.setIn(['settings', 'ticketsTabIsEnforced'], action.value);
      }
    case Actions.SET_EMAIL_SIGNATURES_ENABLED:
      {
        return state.setIn(['settings', 'emailSignaturesEnabled'], action.value);
      }
    case Actions.SET_TOTAL_RELATED_TICKETS_COUNT:
      {
        var _count2 = action.count;
        return state.setIn(['ticketsData', 'totalNumberOfRelatedTickets', action.ticketKey], _count2);
      }
    case Actions.SET_TICKET_ATTACHMENT_SEND_RESTRICTIONS:
      {
        var restrictions = listToMap(fromJS(action.data), function (restriction) {
          return restriction.get('channel');
        });
        return state.setIn(['settings', 'attachments'], restrictions);
      }
    case Actions.SET_TICKET_REPLY_DRAFT:
      {
        var _ticketKey11 = action.ticketKey;
        var replyType = action.replyType;
        if (!action.text) {
          var otherReplyType = replyType === 'internal' ? 'external' : 'internal';
          var otherDraft = state.getIn(['drafts', _ticketKey11, otherReplyType]);

          // If the other reply type is also empty/missing, delete the entire ticket key
          if (!otherDraft || otherDraft.isEmpty()) {
            return state.deleteIn(['drafts', _ticketKey11]);
          }

          // Otherwise, just delete this reply type
          return state.deleteIn(['drafts', _ticketKey11, replyType]);
        }
        return state.setIn(['drafts', _ticketKey11, replyType, 'text'], action.text).setIn(['drafts', _ticketKey11, replyType, 'mentions'], action.mentions);
      }
    case Actions.RESULT_UPDATED_CONTACT:
      {
        var contact = fromJS(action.data);
        var contactKey = contact.get('key');
        if (!contactKey) return state;
        var updateByKey = function updateByKey(byKey) {
          return byKey === null || byKey === void 0 ? void 0 : byKey.map(function (ticket) {
            return ticket.getIn(['visitorDetails', 'key']) === contactKey ? ticket.set('visitorDetails', contact) : ticket;
          });
        };
        return state.updateIn(['ticketsData', 'byKey'], updateByKey).updateIn(['tempSearchData', 'byKey'], updateByKey);
      }
    default:
      {
        return state;
      }
  }
};
export default ticketsReducer;
var updateEventDataDynamicState = function updateEventDataDynamicState(wholeTicketState, ticketKey, dynamicDataByEventKey) {
  dynamicDataByEventKey.forEach(function (data, eventKey) {
    if (data.get('deliveryStatus')) {
      wholeTicketState.setIn(['ticketEvents', 'dataDynamic', ticketKey, eventKey, 'deliveryStatus'], data.get('deliveryStatus'));
    }
    if (data.get('attachments')) {
      wholeTicketState.setIn(['ticketEvents', 'dataDynamic', ticketKey, eventKey, 'attachments'], data.get('attachments'));
    }
  });
};